/* eslint-disable jsdoc/require-returns */
/* eslint-disable jsdoc/require-param */
import {Map, List} from 'immutable';

import {GuideModelV1} from 'resources/augmented/Guide/GuideModel.v1';
import {GuideLevelModelV2} from 'resources/augmented/GuideLevel/GuideLevelModel.v2';

export type GuideTreeType = Map<string, any>;

export type GuideLevelObjectType = {
  guideLevel: GuideLevelModelV2;
  isExpanded: boolean;
  isSelected: boolean;
  isIndeterminate: boolean;
};

export type GuideLevelMapType = Map<string, GuideLevelObjectType>;

export type SortedGuideLevelsType = List<GuideModelV1>;

/**
 * Takes the initial guide and reduces the sorted levels to a Map of ID -> guideLevel
 */
export const createGuideLevelMap = (
  sortedGuideLevels: SortedGuideLevelsType
): GuideLevelMapType => {
  const guideLevelMap = sortedGuideLevels.reduce((acc, guideLevel) => {
    const guideObj = {
      guideLevel,
      isExpanded: false,
      isSelected: false,
      isIndeterminate: false
    };
    // @ts-ignore
    return acc.set(guideLevel.getId(), guideObj);
  }, Map());
  return guideLevelMap as GuideLevelMapType;
};

/**
 * Toggles the expanded property on a guide level
 */
export const toggleExpandGuideLevel = (guideLevelId: string, guideLevelMap: GuideLevelMapType) => {
  const isExpanded = !guideLevelMap.get(guideLevelId).isExpanded;
  const updatedGuideLevelMap = guideLevelMap.update(guideLevelId, (obj) => ({
    ...obj,
    isExpanded
  }));
  return updatedGuideLevelMap;
};

const getKeys = (obj) => {
  const keys = Object.keys(obj).reduce((acc, key) => {
    if (Object.keys(obj[key]).length) {
      acc.push(...getKeys(obj[key]));
    }
    acc.push(key);
    return acc;
  }, [] as Array<string>);
  return keys;
};

/**
 * Handles initial logic of toggling an element and all of its children
 * also updates indeterminate states
 * should return the updated map
 *
 */
export const toggleSelectedGuideLevel = (
  guideLevelId: string,
  selected: boolean | undefined,
  guideLevelMap: GuideLevelMapType,
  guideTree: GuideTreeType
) => {
  const guideLevelObj = guideLevelMap.get(guideLevelId);
  if (!guideLevelObj) {
    return guideLevelMap;
  }
  const isSelected = selected !== undefined ? selected : !guideLevelObj.isSelected;
  const isExpanded = isSelected || guideLevelObj.isExpanded;
  let newGuideLevelMap = guideLevelMap.update(guideLevelId, (obj) => {
    return {
      ...obj,
      isSelected,
      isIndeterminate: false,
      isExpanded
    };
  });
  const lineage = getGuideLineage(guideLevelId, newGuideLevelMap);
  const guideLevel = guideTree.getIn(lineage);
  const flattenedGuideLevelIds = getKeys(guideLevel.toJS());
  flattenedGuideLevelIds.forEach((id) => {
    newGuideLevelMap = newGuideLevelMap.update(id, (obj) => {
      return {
        ...obj,
        isSelected,
        isIndeterminate: false,
        isExpanded
      };
    });
  });
  if (selected === undefined) {
    newGuideLevelMap = checkIndeterminate(guideLevelId, guideTree, newGuideLevelMap);
  }
  return newGuideLevelMap;
};

/**
 * If a theme or topic are selected all children must also be selected
 *
 * @param  {string} guideLevelId
 * @param  {boolean} toggleValue
 */
export const toggleChildrenSelection = (
  guideLevelId: string,
  toggleValue: boolean,
  guideTree: GuideTreeType,
  guideLevelMap: GuideLevelMapType
) => {
  const lineage = getGuideLineage(guideLevelId, guideLevelMap);
  const guideLevel = guideTree.getIn(lineage);
  guideLevel.keySeq().forEach((id) => {
    toggleSelectedGuideLevel(id, toggleValue, guideLevelMap, guideTree);
  });
};

/**
 * Gets the lineage of element being passed in and checks the parent element
 * to decide what selected state it should be recursively runs up the tree
 * until we are at the Theme level
 */
export const checkIndeterminate = (
  guideLevelId: string,
  guideTree: GuideTreeType,
  guideLevelMap: GuideLevelMapType
) => {
  const lineage = getGuideLineage(guideLevelId, guideLevelMap);
  // Lineage includes curr ID and we want to start at parent
  lineage.pop();
  if (!lineage.length) {
    return guideLevelMap;
  }
  let updatedGuideLevelMap = guideLevelMap;
  lineage.reverse().forEach((directParentId) => {
    const parentTree = guideTree.getIn(getGuideLineage(directParentId, updatedGuideLevelMap));
    let selectedElms = 0;
    let unselectedElms = 0;
    let indeterminateElms = false;
    parentTree.forEach((childId, id) => {
      const child = updatedGuideLevelMap.get(id);
      if (child.isIndeterminate) {
        indeterminateElms = true;
      } else if (child.isSelected) {
        selectedElms += 1;
      } else {
        unselectedElms += 1;
      }
    });
    if (
      indeterminateElms ||
      (selectedElms && unselectedElms) ||
      (!unselectedElms && selectedElms !== parentTree.size)
    ) {
      updatedGuideLevelMap = toggleIndeterminateGuideLevel(
        directParentId,
        true,
        updatedGuideLevelMap
      );
    } else {
      const isSelected = !unselectedElms && !indeterminateElms;
      updatedGuideLevelMap = toggleSelectedGuideLevel(
        directParentId,
        isSelected,
        updatedGuideLevelMap,
        guideTree
      );
    }
  });
  return updatedGuideLevelMap;
};

/**
 * handles setting indeterminate property
 *
 */
export const toggleIndeterminateGuideLevel = (
  guideLevelId: string,
  isIndeterminate: boolean,
  guideLevelMap: GuideLevelMapType
) => {
  const updatededGuideLevelMap = guideLevelMap.update(guideLevelId, (obj) => ({
    ...obj,
    isIndeterminate,
    isSelected: true
  }));
  return updatededGuideLevelMap;
};

/**
 * Gets the lineage (parents) of an element to be used within the guideTree including id of passed in element
 */
export const getGuideLineage = (guideLevelId: string, guideLevelMap: GuideLevelMapType) => {
  const lineage: string[] = [];
  let {guideLevel} = guideLevelMap.get(guideLevelId);
  lineage.push(guideLevelId);
  while (guideLevel.getParentId()) {
    lineage.push(guideLevel.getParentId());
    guideLevel = guideLevelMap.get(guideLevel.getParentId()).guideLevel;
  }
  return lineage.reverse();
};

/**
 * initially filters through all levels for matching search string,
 * then adds all IDs of matching levels and their parents and children to a set, to maintain tree integrity
 * and filters the initial guide levels one more time to include all matches, children, and their parents
 */
export const getFilteredGuideList = (
  sortedGuideLevels: SortedGuideLevelsType,
  searchString: string,
  guideLevelMap: GuideLevelMapType,
  guideTree: GuideTreeType
) => {
  const filteredLevels = sortedGuideLevels.filter((guide) => {
    return guide.getName().toLowerCase().indexOf(searchString.toLowerCase()) > -1;
  });
  const filteredIds = filteredLevels.reduce((acc, level) => {
    const lineage = getGuideLineage(level.getId(), guideLevelMap);
    const children = getChildren(lineage[lineage.length - 1], guideLevelMap, guideTree);
    // @ts-ignore
    return new Set([...acc, ...children, ...lineage]);
  }, new Set());

  return sortedGuideLevels.filter((guide) => {
    return filteredIds.has(guide.getId());
  });
};

/**
 * returns an array of ids that are an elements children
 */
export const getChildren = (
  guideLevelId: string,
  guideLevelMap: GuideLevelMapType,
  guideTree: GuideTreeType
) => {
  const lineage = getGuideLineage(guideLevelId, guideLevelMap);
  const guideLevel = guideTree.getIn(lineage);
  return [...guideLevel.keySeq().toArray(), ...guideLevel.flatten(true).keySeq().toArray()];
};

/**
 * Selects all items in the modal
 */
export const selectAll = (guideTree: GuideTreeType, guideLevelMap: GuideLevelMapType) => {
  guideTree.keySeq().forEach((id) => {
    if (id) {
      return toggleSelectedGuideLevel(id, true, guideLevelMap, guideTree);
    }
    return null;
  });
};

/**
 * Resets the modal to have no selections and nothing expanded
 */
export const resetSelections = (activeGuide: GuideModelV1) => {
  return createGuideLevelMap(activeGuide.getSortedGuideLevels());
};

export const selectMatchingParent = (
  selectedElmId: string,
  guideTree: GuideTreeType,
  guideLevelMap: GuideLevelMapType
) => {
  const lineage = getGuideLineage(selectedElmId, guideLevelMap);
  const guideLevel = guideTree.getIn(lineage);

  const selectedElmGuideLevelObj = guideLevelMap.get(selectedElmId);
  const selectedElmIsMatch = selectedElmGuideLevelObj.guideLevel.getMeta().isMatchesSearch();

  let newGuideLevelMap = guideLevelMap;

  if (selectedElmIsMatch) {
    newGuideLevelMap = toggleSelectedGuideLevel(
      selectedElmId,
      undefined,
      newGuideLevelMap,
      guideTree
    );
    return newGuideLevelMap;
  }
  if (childIsMatch(selectedElmId, guideLevelMap, guideTree)) {
    guideLevel.keySeq().forEach((childId) => {
      const childGuideLevelObj = newGuideLevelMap.get(childId);
      const childLineage = getGuideLineage(childId, guideLevelMap);
      const childGuideLevel = guideTree.getIn(childLineage);
      const isMatch = childGuideLevelObj.guideLevel.getMeta().isMatchesSearch();

      if (isMatch) {
        // toggle element and all children if matching
        newGuideLevelMap = toggleSelectedGuideLevel(
          childId,
          undefined,
          newGuideLevelMap,
          guideTree
        );
      } else {
        childGuideLevel.keySeq().forEach((nestedChildId) => {
          const nestedChildObj = newGuideLevelMap.get(nestedChildId);
          const isNestedMatch = nestedChildObj.guideLevel.getMeta().isMatchesSearch();
          // select any matching nested children
          if (isNestedMatch) {
            newGuideLevelMap = toggleSelectedGuideLevel(
              nestedChildId,
              undefined,
              newGuideLevelMap,
              guideTree
            );
          }
        });
      }
    });
  }
  return newGuideLevelMap;
};

export const childIsMatch = (
  id: string,
  guideLevelMap: GuideLevelMapType,
  guideTree: GuideTreeType
) => {
  const children = getChildren(id, guideLevelMap, guideTree);
  return children.some((levelId) =>
    guideLevelMap.get(levelId).guideLevel.getMeta().isMatchesSearch()
  );
};
